import { CellClickedEvent, ColDef, GridOptions } from '@ag-grid-community/core'
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosDefaultColDef,
} from '@helios/ag-grid-config'
import { LayoutContainer } from '@helios/panel-base'
import { RootState } from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi } from '@northfork/ag-grid'
import { ToastService } from '@northfork/ng-basics'
import { ComponentContainer } from 'golden-layout'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { TransactionDetailKey } from '../../panel-key'
import { TpiModalComponent } from '../component/tpi-modal/tpi-modal.component'
import { cwTransactionFieldMap } from '../models/clearwater-transaction.model'
import {
  cwTransactionDetailsField,
  CwTransactionDetailsModel,
  ExternalTransactionDetailsModel,
} from '../models/transaction-details.model'
import { TransferPurchaseInfo } from '../models/transferPurchaseInfo.model'
import { TransactionDetailService } from '../services/transaction-detail.service'
import { formatTooltip } from '@helios/utils'

@Component({
  selector: 'hs-clearwater-transaction-detail-grid',
  templateUrl: './clearwater-transaction-detail-grid.component.html',
  styleUrls: ['./clearwater-transaction-detail-grid.component.css'],
})
export class ClearwaterTransactionDetailGridComponent implements OnDestroy {
  @Output()
  loadingData = new EventEmitter<boolean>()
  /**
   * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
   * preferences for this grid.
   */
  ClearwaterTransactionDetailMainGridKey = `${TransactionDetailKey}-clearwaterTransactionDetailGrid`
  rowExternalTransactionData: ExternalTransactionDetailsModel[] = []
  rowCwTransactionData: CwTransactionDetailsModel[] = []
  private gridApi?: AgGridApi
  private readonly cwTransactioncolDefs: ColDef[] =
    this.createCwTransColumnDefs()
  cwTransactionGridOptions: GridOptions = this.createCwTransactionGridOptions()

  private readonly destroySubject$ = new Subject<void>()

  @Input() set clearwaterTransactionId(transId: string | undefined) {
    this.rowExternalTransactionData = []
    this.rowCwTransactionData = []
    if (transId) {
      this.loadingData.emit(true)
      this.loadCwTransaction(transId)
    }
  }

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    readonly store: Store<RootState>,
    public transactionDetailService: TransactionDetailService,
    private readonly modalService: NgbModal,
    public toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  loadCwTransaction(transId: string) {
    this.transactionDetailService
      .loadTransaction(transId)
      .subscribe(cwTransaction => {
        this.rowCwTransactionData = []
        const transMap = new Map(Object.entries(cwTransaction))
        cwTransactionFieldMap.forEach((value, key) => {
          const valueData = transMap.get(key)
          if (transMap.get(key)) {
            if ('tpiDataMap' == key) {
              const tpiMap = new Map(
                Object.entries<TransferPurchaseInfo>(valueData),
              )
              if (tpiMap.size > 0) {
                this.rowCwTransactionData.push({
                  property: value,
                  value: valueData,
                  key: key,
                })
              }
            } else if ('dataMap' == key) {
              this.rowCwTransactionData.push({
                property: value,
                value: formatTooltip(valueData),
                key: key,
              })
            } else {
              this.rowCwTransactionData.push({
                property: value,
                value: valueData,
                key: key,
              })
            }
          }
        })
        this.loadingData.emit(false)
      })
  }

  private createCwTransColumnDefs(): ColDef[] {
    return [
      {
        field: cwTransactionDetailsField('property'),
        width: 250,
      },
      {
        field: cwTransactionDetailsField('value'),
        width: 800,
        cellRenderer: params => {
          if ('tpiDataMap' == params.data.key) {
            return `<u>View Details</u>`
          } else {
            return params.data.value
          }
        },
      },
    ]
  }

  private createCwTransactionGridOptions(): GridOptions {
    return {
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: true,
        tooltipComponentParams: {
          showTooltip: () => true,
        },
      },
      enableBrowserTooltips: true,
      columnDefs: this.cwTransactioncolDefs,
      onCellClicked: (event: CellClickedEvent) => {
        if ('tpiDataMap' == event.data.key) {
          this.showTpiModal(event.data.value)
        }
      },
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onGridReady: params => {
        this.gridApi = params
        this.savedViewService.manageSavedView(
          params,
          this.ClearwaterTransactionDetailMainGridKey,
          this.destroySubject$,
        )
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
        enableHeliosCopyBehavior(params.api)
      },
    }
  }

  showTpiModal(tpiData: { [key: number]: TransferPurchaseInfo }) {
    const modalRef = this.modalService.open(TpiModalComponent, {
      keyboard: false,
    } as NgbModalOptions)
    modalRef.componentInstance.tpiModalInput = {
      tpiDataMap: tpiData,
    }
  }
}
